import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Switch, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { RequestFormSelectField } from '../../components/RequestFormSelectField';
import { ScreenHeader } from '../../components/ScreenHeader';
import { SETTINGS_CAPABILITIES } from '../../config/settingsCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  emptyLateArrivalConfiguration,
  getLateArrivalConfiguration,
  saveLateArrivalConfiguration,
} from '../../services/lateArrivalConfigurationService';
import type {
  LateArrivalConfiguration,
  LateArrivalRule,
} from '../../types/lateArrivalConfiguration';
import { normalizeApiError } from '../../api/apiError';
import { formatMinutes } from '../../utils/dateTime';
import { occurrenceRangeLabel, ruleSetError } from '../../utils/lateArrivalRules';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'LateArrivalConfiguration'
>;

type RuleDraft = {
  key: string;
  occurrenceFromText: string;
  occurrenceToText: string;
  leaveTypeId: string;
  deductionDaysText: string;
  lopOnInsufficientBalance: boolean;
};

let draftKeySeed = 0;
function nextDraftKey(): string {
  draftKeySeed += 1;
  return `rule-${draftKeySeed}`;
}

function draftFromRule(rule: LateArrivalRule): RuleDraft {
  return {
    key: nextDraftKey(),
    occurrenceFromText: String(rule.occurrenceFrom),
    occurrenceToText: rule.occurrenceTo === null ? '' : String(rule.occurrenceTo),
    leaveTypeId: rule.leaveTypeId === null ? '' : String(rule.leaveTypeId),
    deductionDaysText: rule.deductionDays > 0 ? String(rule.deductionDays) : '',
    lopOnInsufficientBalance: rule.lopOnInsufficientBalance,
  };
}

function wholeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

function decimalDays(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return parsed > 0 ? parsed : null;
}

type ParsedRule = {
  occurrenceFrom: number;
  occurrenceTo: number | null;
  leaveTypeId: number;
  deductionDays: number;
  lopOnInsufficientBalance: boolean;
};

function parseDrafts(drafts: RuleDraft[]): { rules: ParsedRule[] } | { error: string } {
  const rules: ParsedRule[] = [];
  for (const [index, draft] of drafts.entries()) {
    const label = `Rule ${index + 1}`;
    const occurrenceFrom = wholeNumber(draft.occurrenceFromText);
    if (occurrenceFrom === null || occurrenceFrom < 1) {
      return { error: `${label}: enter the late arrival number it starts from.` };
    }
    const occurrenceTo = draft.occurrenceToText.trim()
      ? wholeNumber(draft.occurrenceToText)
      : null;
    if (draft.occurrenceToText.trim() && (occurrenceTo === null || occurrenceTo < occurrenceFrom)) {
      return { error: `${label}: the end must be empty or not before the start.` };
    }
    const leaveTypeId = wholeNumber(draft.leaveTypeId);
    if (leaveTypeId === null) {
      return { error: `${label}: select the leave type to deduct.` };
    }
    const deductionDays = decimalDays(draft.deductionDaysText);
    if (deductionDays === null) {
      return { error: `${label}: enter days to deduct, like 0.25 or 0.5.` };
    }
    rules.push({
      occurrenceFrom,
      occurrenceTo,
      leaveTypeId,
      deductionDays,
      lopOnInsufficientBalance: draft.lopOnInsufficientBalance,
    });
  }
  const rangeError = ruleSetError(rules);
  return rangeError ? { error: rangeError } : { rules };
}

export function LateArrivalConfigurationScreen({ navigation }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const canUpdate = auth.hasPermission(PERMISSIONS.SETTINGS_UPDATE);
  const [config, setConfig] = useState<LateArrivalConfiguration>(
    emptyLateArrivalConfiguration(),
  );
  const [drafts, setDrafts] = useState<RuleDraft[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const themed = useMemo(
    () => ({
      caption: { ...typography.caption, color: palette.textMuted },
      label: { ...typography.body, color: palette.textPrimary, flex: 1 },
      section: { ...typography.sectionTitle, color: palette.textPrimary },
      error: { ...typography.caption, color: palette.error },
    }),
    [palette],
  );

  const leaveTypeOptions = useMemo(
    () =>
      config.leaveTypes.map(type => ({
        value: String(type.leaveTypeId),
        label: type.leaveTypeName || type.leaveTypeCode,
      })),
    [config.leaveTypes],
  );

  const applyConfiguration = useCallback((next: LateArrivalConfiguration) => {
    setConfig(next);
    setDrafts(next.rules.map(draftFromRule));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      applyConfiguration(await getLateArrivalConfiguration());
      setLoaded(true);
      setError(null);
    } catch (caught) {
      setError(normalizeApiError(caught).message);
    } finally {
      setLoading(false);
    }
  }, [applyConfiguration]);

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  function updateDraft(key: string, patch: Partial<RuleDraft>) {
    setDrafts(current =>
      current.map(draft => (draft.key === key ? { ...draft, ...patch } : draft)),
    );
  }

  function addRule() {
    setDrafts(current => {
      const last = current[current.length - 1];
      const lastEnd = last ? wholeNumber(last.occurrenceToText) : 0;
      const start = last ? (lastEnd ?? wholeNumber(last.occurrenceFromText) ?? 0) + 1 : 1;
      const closedLast =
        last && !last.occurrenceToText.trim()
          ? current.map(draft =>
              draft.key === last.key
                ? { ...draft, occurrenceToText: String(start - 1) }
                : draft,
            )
          : current;
      return [
        ...closedLast,
        {
          key: nextDraftKey(),
          occurrenceFromText: String(start),
          occurrenceToText: String(start),
          leaveTypeId: '',
          deductionDaysText: '',
          lopOnInsufficientBalance: true,
        },
      ];
    });
  }

  function removeRule(key: string) {
    setDrafts(current => current.filter(draft => draft.key !== key));
  }

  async function submit() {
    const parsed = parseDrafts(drafts);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    if (config.isEnabled && parsed.rules.length === 0) {
      setError('Add at least one late arrival rule before enabling late arrival.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const saved = await runOperation('update', () => saveLateArrivalConfiguration({
        isEnabled: config.isEnabled,
        rules: parsed.rules,
        rowVersion: config.rowVersion,
      }));
      applyConfiguration(saved);
      navigation.goBack();
    } catch (caught) {
      setError(normalizeApiError(caught).message);
    } finally {
      setSaving(false);
    }
  }

  const editable = SETTINGS_CAPABILITIES.lateArrivalConfiguration && canUpdate;

  return (
    <PermissionGuard
      permission={PERMISSIONS.SETTINGS_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen scroll>
        <ScreenHeader title="Late Arrival Configuration" onBack={navigation.goBack} />
        {loading && !loaded ? (
          <AppLoader message="Loading late arrival settings" />
        ) : error && !loaded ? (
          <ErrorState message={error} onRetry={() => load()} />
        ) : (
          <View style={styles.body}>
            <Text style={themed.caption}>
              A check-in counts as a late arrival when it is later than the shift
              start by more than the work policy's late grace minutes. Late
              arrivals are counted per employee for each calendar month and
              deducted from leave using the rules below.
            </Text>
            <AppCard style={styles.card}>
              <View style={styles.row}>
                <Text style={themed.label}>Enable late arrival rules</Text>
                <Switch
                  disabled={!editable}
                  value={config.isEnabled}
                  onValueChange={value =>
                    setConfig(current => ({ ...current, isEnabled: value }))
                  }
                />
              </View>
            </AppCard>

            <AppCard style={styles.card}>
              <Text style={themed.section}>Late threshold by work policy</Text>
              {config.workPolicies.length === 0 ? (
                <Text style={themed.caption}>
                  No active work policy is configured. Set late grace minutes in
                  Work Policies.
                </Text>
              ) : (
                config.workPolicies.map(policy => (
                  <Text key={policy.workPolicyId} style={themed.caption}>
                    {policy.policyName || policy.policyCode}
                    {policy.isDefault ? ' (default)' : ''}:{' '}
                    {policy.lateGraceMinutes === null
                      ? 'not set'
                      : `late after ${formatMinutes(policy.lateGraceMinutes)}`}
                  </Text>
                ))
              )}
            </AppCard>

            <Text style={themed.section}>Deduction rules</Text>
            {drafts.length === 0 ? (
              <Text style={themed.caption}>No late arrival rules yet.</Text>
            ) : null}
            {drafts.map((draft, index) => {
              const from = wholeNumber(draft.occurrenceFromText);
              const to = draft.occurrenceToText.trim()
                ? wholeNumber(draft.occurrenceToText)
                : null;
              const heading =
                from !== null
                  ? `${occurrenceRangeLabel({ occurrenceFrom: from, occurrenceTo: to })} late arrival`
                  : `Rule ${index + 1}`;
              return (
                <AppCard key={draft.key} style={styles.card}>
                  <Text style={themed.label}>{heading}</Text>
                  <View style={styles.inline}>
                    <View style={styles.inlineField}>
                      <AppInput
                        label="From late arrival #"
                        value={draft.occurrenceFromText}
                        editable={editable}
                        keyboardType="number-pad"
                        onChangeText={value =>
                          updateDraft(draft.key, { occurrenceFromText: value })
                        }
                      />
                    </View>
                    <View style={styles.inlineField}>
                      <AppInput
                        label="To late arrival #"
                        value={draft.occurrenceToText}
                        editable={editable}
                        keyboardType="number-pad"
                        placeholder="Blank = and later"
                        onChangeText={value =>
                          updateDraft(draft.key, { occurrenceToText: value })
                        }
                      />
                    </View>
                  </View>
                  <RequestFormSelectField
                    label="Deduct from leave type"
                    required
                    value={draft.leaveTypeId}
                    options={leaveTypeOptions}
                    editable={editable}
                    onChange={value => updateDraft(draft.key, { leaveTypeId: value })}
                  />
                  <AppInput
                    label="Days to deduct"
                    value={draft.deductionDaysText}
                    editable={editable}
                    keyboardType="decimal-pad"
                    onChangeText={value =>
                      updateDraft(draft.key, { deductionDaysText: value })
                    }
                  />
                  <View style={styles.row}>
                    <Text style={themed.label}>
                      Insufficient balance becomes LOP
                    </Text>
                    <Switch
                      disabled={!editable}
                      value={draft.lopOnInsufficientBalance}
                      onValueChange={value =>
                        updateDraft(draft.key, { lopOnInsufficientBalance: value })
                      }
                    />
                  </View>
                  {editable ? (
                    <AppButton
                      title="Remove rule"
                      variant="secondary"
                      onPress={() => removeRule(draft.key)}
                    />
                  ) : null}
                </AppCard>
              );
            })}
            {editable ? (
              <AppButton title="Add rule" variant="secondary" onPress={addRule} />
            ) : null}

            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}
            {editable ? (
              <AppButton
                title="Save late arrival configuration"
                onPress={submit}
                loading={saving}
              />
            ) : null}
          </View>
        )}
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.md, paddingBottom: spacing.xl },
  card: { gap: spacing.md },
  row: {
    minHeight: 48,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  inline: { flexDirection: 'row', gap: spacing.md },
  inlineField: { flex: 1 },
});
